import React from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Modal from '../ui/Modal';

/* ── Date formatting helper ─────────────────────────────────────────────── */
function formatLongDate(dateStr) {
  if (!dateStr) return 'August 31 , 2026';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December',
    ];
    return `${months[d.getMonth()]} ${d.getDate()} , ${d.getFullYear()}`;
  } catch (e) {
    return dateStr;
  }
}

function formatShortDate(dateStr) {
  if (!dateStr) return '31-08-2026';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  } catch (e) {
    return dateStr;
  }
}

function formatPDFCurrency(num) {
  if (num == null || isNaN(num)) return 'Rs. 0/-';
  return `Rs. ${Number(num).toLocaleString('en-IN')}/-`;
}

function formatHTMLCurrency(num) {
  if (num == null || isNaN(num)) return '₹ 0/-';
  return `₹ ${Number(num).toLocaleString('en-IN')}/-`;
}

/* ── 360 Fashion Logo Drawer on jsPDF ─────────────────────────────────────── */
function draw360Logo(doc, x, y) {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(30);

  // '3' Red-Orange
  doc.setTextColor(232, 76, 24);
  doc.text('3', x, y + 12);

  // '6' Green
  doc.setTextColor(18, 168, 52);
  doc.text('6', x + 10, y + 12);

  // '0' Golden Yellow
  doc.setTextColor(245, 166, 35);
  doc.text('0', x + 20, y + 12);

  // Degree symbol
  doc.setTextColor(20, 20, 20);
  doc.setFontSize(14);
  doc.text('o', x + 29, y + 3);

  // 'F A S H I O N'
  doc.setFontSize(8);
  doc.setFont('times', 'bold');
  doc.setTextColor(20, 20, 20);
  doc.text('F  A  S  H  I  O  N', x + 1, y + 19);
}

/* ── Core PDF Generator ─────────────────────────────────────────────────── */
export async function downloadInvoicePDF({
  entry = {},
  engagement = {},
  manufacturer = {},
  brand = {},
  fileName = 'Invoice',
}) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth(); // 210mm
  const margin = 14;

  /* ── 1. Top Header ──────────────────────────────────────────────────────── */
  // Left: 360 Logo
  draw360Logo(doc, margin, 14);

  // Right: Seller Details (360 Degree Fashion Boutique)
  const sellerName = manufacturer?.name || '360 Degree Fashion Boutique';
  const sellerAddrLine1 = 'DGN Complex Shop no : 3, Door no : 2/2B,';
  const sellerAddrLine2 = 'Agaramthen To Kovilanchery Main Road, Agaramthen,';
  const sellerAddrLine3 = 'Chennai - 600126';
  const sellerPhone = manufacturer?.phone || '9876567898';
  const sellerGstin = manufacturer?.gstNumber || '33ALCPN6280A2ZH';

  doc.setFont('times', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(27, 50, 82); // Dark Navy Blue
  doc.text(sellerName, pageWidth - margin, 18, { align: 'right' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(40, 40, 40);
  doc.text(sellerAddrLine1, pageWidth - margin, 24, { align: 'right' });
  doc.text(sellerAddrLine2, pageWidth - margin, 28, { align: 'right' });
  doc.text(sellerAddrLine3, pageWidth - margin, 32, { align: 'right' });
  doc.text(sellerPhone, pageWidth - margin, 37, { align: 'right' });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9.5);
  doc.setTextColor(0, 0, 0);
  doc.text(`GSTIN ${sellerGstin}`, pageWidth - margin, 43, { align: 'right' });

  /* ── 2. Bill To & Invoice Info ─────────────────────────────────────────── */
  const buyerName = brand?.legalName || brand?.name || 'RANJANI SEKAR';
  const buyerAddress = brand?.location || 'NO 26 B, EVP Rajeshwari Avenue, Thiruveni Street,\nMadhanandapuram Mugalivakkam, Chennai, Chengalpattu,\nTamil Nadu, 600125';
  const buyerGstin = brand?.gstNumber || '33JUUPS5178A1Z1';

  const preparedDate = formatLongDate(entry?.date || entry?.postingDate);
  const invoiceNo = entry?.reference || `KIT/26-27/08-${String(entry?.id || '001').padStart(3, '0')}`;

  let cursorY = 56;

  // Left: Bill To
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(20, 20, 20);
  doc.text('BILL TO', margin, cursorY);

  doc.setFontSize(9.5);
  doc.text(buyerName, margin, cursorY + 4.5);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(50, 50, 50);
  const addrLines = doc.splitTextToSize(buyerAddress, 95);
  doc.text(addrLines, margin, cursorY + 9);

  const afterAddrY = cursorY + 9 + addrLines.length * 4;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 0);
  doc.text(`GST IN: ${buyerGstin}`, margin, afterAddrY + 2);

  // Right: Prepared Date & Invoice No
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(20, 20, 20);
  doc.text('PREPARED DATE', pageWidth - margin, cursorY, { align: 'right' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(40, 40, 40);
  doc.text(preparedDate, pageWidth - margin, cursorY + 4.5, { align: 'right' });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(20, 20, 20);
  doc.text('INVOICE NO :', pageWidth - margin, cursorY + 12, { align: 'right' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(40, 40, 40);
  doc.text(invoiceNo, pageWidth - margin, cursorY + 16.5, { align: 'right' });

  /* ── 3. Table Calculation ──────────────────────────────────────────────── */
  const rawAmount = Number(entry?.amount || entry?.debit || entry?.credit || 50000);
  const baseAmount = rawAmount >= 59000 ? Math.round(rawAmount / 1.18) : rawAmount;
  const cgstAmount = Math.round(baseAmount * 0.09);
  const sgstAmount = Math.round(baseAmount * 0.09);
  const grandTotal = baseAmount + cgstAmount + sgstAmount;

  const itemDesc = entry?.description || '1. 360Degree Fashion Brand Outsource Program 1 time signup fee';
  const hsnCode = entry?.hsn || '998422';

  const tableStartY = Math.max(afterAddrY + 8, cursorY + 24);

  autoTable(doc, {
    startY: tableStartY,
    head: [['ITEM', 'HSN/SAN', 'TOTAL']],
    body: [
      [itemDesc, hsnCode, formatPDFCurrency(baseAmount)],
      [{ content: 'Total Amount ( Excl. GST)', styles: { fontStyle: 'bold', halign: 'center' } }, '', { content: formatPDFCurrency(baseAmount), styles: { fontStyle: 'bold', halign: 'right' } }],
      [{ content: 'C GST 9%', styles: { fontStyle: 'bold', halign: 'center' } }, '', { content: formatPDFCurrency(cgstAmount), styles: { fontStyle: 'bold', halign: 'right' } }],
      [{ content: 'S GST 9%', styles: { fontStyle: 'bold', halign: 'center' } }, '', { content: formatPDFCurrency(sgstAmount), styles: { fontStyle: 'bold', halign: 'right' } }],
      [{ content: 'GRAND TOTAL', styles: { fontStyle: 'bold', halign: 'center' } }, '', { content: formatPDFCurrency(grandTotal), styles: { fontStyle: 'bold', halign: 'right' } }],
    ],
    theme: 'grid',
    styles: {
      fontSize: 8.5,
      cellPadding: { top: 3.5, bottom: 3.5, left: 3, right: 3 },
      textColor: [20, 20, 20],
      lineColor: [215, 215, 215],
      lineWidth: 0.2,
      valign: 'middle',
      overflow: 'linebreak',
    },
    headStyles: {
      fillColor: [205, 179, 158], // Tan / warm beige header background
      textColor: [35, 45, 60],
      fontStyle: 'bold',
      fontSize: 9,
    },
    columnStyles: {
      0: { cellWidth: 105, halign: 'left' },
      1: { cellWidth: 32, halign: 'center' },
      2: { cellWidth: 45, halign: 'right' },
    },
    margin: { left: margin, right: margin },
  });

  /* ── 4. Horizontal Line & Signatures Section ───────────────────────────── */
  const tableEndY = doc.lastAutoTable.finalY + 25;
  const sigStartY = Math.max(tableEndY, 215);

  // Black dividing line
  doc.setDrawColor(40, 40, 40);
  doc.setLineWidth(0.5);
  doc.line(margin, sigStartY - 10, pageWidth - margin, sigStartY - 10);

  // Title: AGREED AND ACCEPTED:
  doc.setFont('times', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(27, 50, 82);
  doc.text('AGREED AND ACCEPTED:', margin, sigStartY);

  // For 360 Degree Fashion Boutique
  doc.setFontSize(11);
  doc.text(`For ${sellerName}`, margin, sigStartY + 6);

  // Signature lines coordinates
  const col1X = margin;
  const col2X = margin + 65;
  const col3X = pageWidth - margin - 50;
  const lineY = sigStartY + 28;

  // "Proprietor" text above 1st signature
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(20, 20, 20);
  doc.text('Proprietor', col1X + 45, lineY - 4, { align: 'right' });

  // Date above 3rd signature line
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(30, 30, 30);
  doc.text(formatShortDate(entry?.date || entry?.postingDate), col3X, lineY - 4);

  // Horizontal signature lines
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.3);
  doc.line(col1X, lineY, col1X + 55, lineY);
  doc.line(col2X, lineY, col2X + 55, lineY);
  doc.line(col3X, lineY, pageWidth - margin, lineY);

  // Labels below lines
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(50, 50, 50);
  doc.text('360 DEGREE FASHION SIGNATORY', col1X, lineY + 5);
  doc.text('CUSTOMER SIGN', col2X, lineY + 5);
  doc.text('DATE', col3X, lineY + 5);

  /* ── 5. Save and Download ──────────────────────────────────────────────── */
  const safeName = (invoiceNo || fileName).replace(/[^a-z0-9_-]/gi, '_');
  doc.save(`${safeName}.pdf`);
}

/* ── Interactive Invoice Preview / Download Component ─────────────────────── */
export default function Invoice({ open, onClose, entry, engagement, manufacturer, brand }) {
  if (!open || !entry) return null;

  const handleDownload = () => {
    downloadInvoicePDF({ entry, engagement, manufacturer, brand });
  };

  const rawAmount = Number(entry?.amount || entry?.debit || entry?.credit || 50000);
  const baseAmount = rawAmount >= 59000 ? Math.round(rawAmount / 1.18) : rawAmount;
  const cgstAmount = Math.round(baseAmount * 0.09);
  const sgstAmount = Math.round(baseAmount * 0.09);
  const grandTotal = baseAmount + cgstAmount + sgstAmount;

  return (
    <Modal open={open} onClose={onClose} title="Invoice" subtitle={entry.reference || `Entry #${entry.id}`} size="lg">
      <div className="bg-white border border-gray-200 rounded-xl p-6 sm:p-8 shadow-sm space-y-6 text-sm font-sans">
        {/* Header */}
        <div className="flex justify-between items-start pb-4 border-b border-gray-100">
          <div>
            <div className="inline-flex flex-col items-center">
              <div className="flex items-baseline font-black leading-none text-3xl">
                <span className="text-[#E84C18]">3</span>
                <span className="text-[#12A834]">6</span>
                <span className="text-[#F5A623]">0</span>
                <span className="text-black text-sm font-bold align-top ml-0.5 relative -top-3">°</span>
              </div>
              <div className="text-[8px] tracking-[0.35em] font-serif font-bold text-gray-900 mt-1 pl-1">
                FASHION
              </div>
            </div>
          </div>
          <div className="text-right">
            <h2 className="font-serif font-bold text-[#1b3252] text-base">{manufacturer?.name || '360 Degree Fashion Boutique'}</h2>
            <p className="text-[11px] text-gray-600 mt-0.5">DGN Complex Shop no : 3, Door no : 2/2B,</p>
            <p className="text-[11px] text-gray-600">Agaramthen To Kovilanchery Main Road, Agaramthen,</p>
            <p className="text-[11px] text-gray-600">Chennai - 600126</p>
            <p className="text-[11px] text-gray-700">{manufacturer?.phone || '9876567898'}</p>
            <p className="text-xs font-bold text-gray-900 mt-1">GSTIN {manufacturer?.gstNumber || '33ALCPN6280A2ZH'}</p>
          </div>
        </div>

        {/* Bill To & Invoice Meta */}
        <div className="flex justify-between items-start pt-2">
          <div>
            <p className="text-xs font-bold text-gray-900 uppercase">BILL TO</p>
            <p className="font-bold text-gray-900 text-sm mt-0.5">{brand?.legalName || brand?.name || 'RANJANI SEKAR'}</p>
            <p className="text-xs text-gray-600 mt-0.5 max-w-xs leading-relaxed whitespace-pre-line">
              {brand?.location || 'NO 26 B, EVP Rajeshwari Avenue, Thiruveni Street,\nMadhanandapuram Mugalivakkam, Chennai, Chengalpattu,\nTamil Nadu, 600125'}
            </p>
            <p className="text-xs font-bold text-gray-900 mt-1.5">GST IN: {brand?.gstNumber || '33JUUPS5178A1Z1'}</p>
          </div>
          <div className="text-right space-y-3">
            <div>
              <p className="text-xs font-bold text-gray-900 uppercase">PREPARED DATE</p>
              <p className="text-xs text-gray-700 mt-0.5">{formatLongDate(entry.date)}</p>
            </div>
            <div>
              <p className="text-xs font-bold text-gray-900 uppercase">INVOICE NO :</p>
              <p className="text-xs font-mono text-gray-900 font-semibold mt-0.5">{entry.reference || `KIT/26-27/08-${String(entry.id).padStart(3, '0')}`}</p>
            </div>
          </div>
        </div>

        {/* Items Table */}
        <div className="border border-gray-200 rounded-sm overflow-hidden mt-4">
          <table className="w-full text-xs text-left border-collapse">
            <thead className="bg-[#CDB39E] text-[#232D3C] font-bold uppercase text-[11px] border-b border-gray-300">
              <tr>
                <th className="px-4 py-2.5 border-r border-gray-300">ITEM</th>
                <th className="px-4 py-2.5 text-center border-r border-gray-300 w-32">HSN/SAN</th>
                <th className="px-4 py-2.5 text-right w-36">TOTAL</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <tr>
                <td className="px-4 py-3 text-gray-800 border-r border-gray-200">{entry.description || '1. 360Degree Fashion Brand Outsource Program 1 time signup fee'}</td>
                <td className="px-4 py-3 text-center font-mono text-gray-700 border-r border-gray-200">998422</td>
                <td className="px-4 py-3 text-right font-mono text-gray-900 font-medium">{formatHTMLCurrency(baseAmount)}</td>
              </tr>
              <tr className="bg-gray-50/50">
                <td className="px-4 py-2 text-center text-gray-800 font-bold border-r border-gray-200">Total Amount ( Excl. GST)</td>
                <td className="px-4 py-2 border-r border-gray-200"></td>
                <td className="px-4 py-2 text-right font-mono font-bold text-gray-900">{formatHTMLCurrency(baseAmount)}</td>
              </tr>
              <tr className="bg-gray-50/50">
                <td className="px-4 py-2 text-center text-gray-800 font-bold border-r border-gray-200">C GST 9%</td>
                <td className="px-4 py-2 border-r border-gray-200"></td>
                <td className="px-4 py-2 text-right font-mono font-bold text-gray-900">{formatHTMLCurrency(cgstAmount)}</td>
              </tr>
              <tr className="bg-gray-50/50">
                <td className="px-4 py-2 text-center text-gray-800 font-bold border-r border-gray-200">S GST 9%</td>
                <td className="px-4 py-2 border-r border-gray-200"></td>
                <td className="px-4 py-2 text-right font-mono font-bold text-gray-900">{formatHTMLCurrency(sgstAmount)}</td>
              </tr>
              <tr className="bg-gray-100 font-bold text-xs">
                <td className="px-4 py-2.5 text-center text-gray-900 uppercase border-r border-gray-300">GRAND TOTAL</td>
                <td className="px-4 py-2.5 border-r border-gray-300"></td>
                <td className="px-4 py-2.5 text-right font-mono text-gray-900">{formatHTMLCurrency(grandTotal)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Footer & Signatures */}
        <div className="pt-6 border-t-2 border-gray-800 space-y-4">
          <div>
            <h3 className="font-serif font-bold text-[#1b3252] text-sm tracking-wide uppercase">AGREED AND ACCEPTED:</h3>
            <p className="font-serif font-bold text-xs text-[#1b3252] mt-0.5">For {manufacturer?.name || '360 Degree Fashion Boutique'}</p>
          </div>

          <div className="grid grid-cols-3 gap-6 pt-8 text-center text-xs">
            <div>
              <p className="text-[11px] font-bold text-right pr-2 text-gray-800 mb-1">Proprietor</p>
              <div className="border-t border-gray-300 pt-1.5 text-[10px] font-medium text-gray-600 uppercase">360 DEGREE FASHION SIGNATORY</div>
            </div>
            <div className="flex flex-col justify-end">
              <div className="border-t border-gray-300 pt-1.5 text-[10px] font-medium text-gray-600 uppercase">CUSTOMER SIGN</div>
            </div>
            <div>
              <p className="text-[11px] text-gray-800 mb-1 font-mono">{formatShortDate(entry.date)}</p>
              <div className="border-t border-gray-300 pt-1.5 text-[10px] font-medium text-gray-600 uppercase">DATE</div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-4 border-t border-gray-100">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            Close
          </button>
          <button onClick={handleDownload} className="flex-1 px-4 py-2.5 text-xs font-medium text-white bg-[#1b3a6b] rounded-lg hover:bg-[#152d55] flex items-center justify-center gap-1.5 transition-colors shadow-sm">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Download PDF Invoice
          </button>
        </div>
      </div>
    </Modal>
  );
}